import { useMemo, useState } from 'react'
import { useUserStore } from '@/store/UserStore'
import { ListStorageVolumeDataItem } from '@/services/inference.gen'
import { ManageStorageHeader } from './Header'
import { StorageCard } from './StorageCard'
import { useListStorageVolumes } from '@/services/storage'
import { StorageViewSortKeys, StorageViewType } from './types'
import { Virtuoso } from 'react-virtuoso'
import { ServerCog, RefreshCw } from 'lucide-react'
import { ErrorMessage } from '@/components/error-message'
import { LoadingMessage } from '@/components/loading-message'
import { useTokenAndOrgId } from '@/services/utils'
import { SetupStorageDialog } from './SetupStorageDialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export const DashboardStorage = () => {
  const { user } = useUserStore()
  const currentUserEmail = user?.email
  const { orgId } = useTokenAndOrgId()

  const [storageView, onStorageViewChange] = useState(
    StorageViewType.MY_STORAGE
  )
  const [storageSort, onStorageSortChange] = useState(
    StorageViewSortKeys.CREATED_AT_DESC
  )
  const [searchTerm, setSearchTerm] = useState('')

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useListStorageVolumes({
    sort: storageSort,
    searchTerm,
    showAllUsers: storageView !== StorageViewType.MY_STORAGE,
    showPublic: storageView === StorageViewType.PUBLIC_STORAGE,
    autoRefreshEnabled: true,
  })

  const allVolumes = useMemo(
    () =>
      ((data?.allVolumes as ListStorageVolumeDataItem[]) || []).filter(
        (volume: ListStorageVolumeDataItem) => !volume.storage_volume.is_deleted
      ),
    [data]
  )
  const totalVolumes = data?.total || 0

  const myVolumesList: ListStorageVolumeDataItem[] = useMemo(() => {
    return allVolumes.filter(
      (volume: ListStorageVolumeDataItem) =>
        volume.storage_volume.user?.email === currentUserEmail
    )
  }, [allVolumes, currentUserEmail])

  const publicVolumesList: ListStorageVolumeDataItem[] = useMemo(() => {
    return allVolumes.filter(
      (volume: ListStorageVolumeDataItem) =>
        volume.storage_volume.is_public && !volume.storage_volume.is_deleted
    )
  }, [allVolumes])

  const currentVolumes: ListStorageVolumeDataItem[] =
    storageView === StorageViewType.MY_STORAGE
      ? myVolumesList
      : storageView === StorageViewType.PUBLIC_STORAGE
        ? publicVolumesList
        : allVolumes

  return (
    <div className="py-4">
      <ManageStorageHeader
        storageViewType={storageView}
        onStorageViewChange={onStorageViewChange}
        storageViewSortType={storageSort}
        onStorageViewSortChange={onStorageSortChange}
        onSearchChange={setSearchTerm}
      />

      {/* Display the total count below the header */}
      {!isLoading && !isError && totalVolumes > 0 && (
        <div
          className="flex items-center gap-2 ml-4 my-2"
          data-testid="storage-count-info"
        >
          <div
            className="text-sm text-muted-foreground"
            data-testid="storage-count-text"
          >
            Showing {currentVolumes.length} of {totalVolumes} storage volumes
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 hover:bg-muted"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw
              className={cn('h-4 w-4', isRefetching && 'animate-spin')}
            />
          </Button>
        </div>
      )}

      {isLoading ? (
        <LoadingMessage message="Loading storage volumes..." centered />
      ) : isError ? (
        <ErrorMessage
          message="Failed to load storage volumes."
          className="mx-auto text-center"
        />
      ) : currentVolumes.length === 0 ? (
        <div
          className="mx-auto flex flex-col items-center gap-16 mt-28"
          data-testid="storage-empty-state"
        >
          {searchTerm.length > 0 ? (
            <h3
              className="text-4xl font-medium"
              data-testid="no-search-results"
            >
              No search results.
            </h3>
          ) : storageView === StorageViewType.MY_STORAGE ? (
            <>
              <h3 className="text-2xl font-medium">
                You have no storage volumes.
              </h3>
              <ServerCog className="w-20 h-20 text-muted-foreground/50" />
            </>
          ) : (
            <>
              <h3 className="text-2xl font-medium">
                None of the Users have any storage volumes.
              </h3>
              <ServerCog className="w-20 h-20 text-muted-foreground/50" />
              <p className="text-muted-foreground max-w-md text-center">
                None of the users in this organization have any storage volumes.
              </p>
            </>
          )}

          <SetupStorageDialog orgId={orgId} />
        </div>
      ) : (
        <Virtuoso
          useWindowScroll
          data={currentVolumes}
          endReached={() => {
            if (hasNextPage) {
              fetchNextPage()
            }
          }}
          key={JSON.stringify(currentVolumes)}
          computeItemKey={(volume) => JSON.stringify(volume)}
          itemContent={(index, volume) => {
            return (
              <div
                key={index}
                className="my-4"
                data-testid={`storage-card-${volume.storage_volume.volume_id}`}
              >
                <StorageCard {...volume} allVolumes={allVolumes} />
              </div>
            )
          }}
          components={{
            Footer: () => {
              return (
                <div className="text-center py-8">
                  {isFetchingNextPage ? (
                    <LoadingMessage message="Loading more..." size="sm" />
                  ) : hasNextPage ? null : (
                    <p className="text-sm text-foreground/50">End of list.</p>
                  )}
                </div>
              )
            },
          }}
        />
      )}
    </div>
  )
}
